/**
 * The display currency, on the caller's own profile — this module's
 * contribution to core's `profile.sections` slot.
 *
 * Emptying the field is an answer, not a mistake: no display currency means
 * every amount shows in its own currency, converted to nothing.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CurrencyPicker } from '../../components/CurrencyPicker';
import { ErrorState } from '../../components/ErrorState';
import { Panel } from '../../components/Panel';
import { useAsync } from '../../hooks/useAsync';
import { useSubmit } from '../../hooks/useSubmit';
import { getPreference, setPreference } from './api';
import { useCurrencies } from './useCurrencies';

export function DisplayCurrencyPanel() {
  const { t } = useTranslation(['currency', 'common']);
  const currencies = useCurrencies();
  const preference = useAsync(getPreference, []);
  const stored = preference.data?.preference.displayCurrency ?? null;

  // Undefined: untouched, so the field follows what is stored.
  const [draft, setDraft] = useState<string | null | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const value = draft === undefined ? stored : draft;

  const save = useSubmit(async () => {
    const response = await setPreference(value);
    preference.setData(response);
    setDraft(undefined);
    setSaved(true);
    return response;
  });

  return (
    <Panel icon={<CurrencyExchangeIcon fontSize="small" />} title={t('currency:preference.title')}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('currency:preference.description')}
        </Typography>

        {preference.error ? (
          <ErrorState error={preference.error} severity="warning" />
        ) : (
          <CurrencyPicker
            options={currencies.codes}
            value={value}
            clearable
            loading={currencies.loading || preference.loading}
            disabled={preference.loading}
            label={t('currency:preference.label')}
            placeholder={t('currency:preference.none')}
            helperText={t('currency:preference.help')}
            onChange={(code) => {
              setDraft(code);
              setSaved(false);
            }}
          />
        )}

        {save.error && <ErrorState error={save.error} severity="warning" />}
        {saved && <Alert severity="success">{t('common:state.saved')}</Alert>}

        <Box>
          <Button
            variant="contained"
            disabled={save.pending || preference.loading || value === stored}
            onClick={() => void save.submit()}
          >
            {save.pending ? t('common:action.saving') : t('common:action.save')}
          </Button>
        </Box>
      </Stack>
    </Panel>
  );
}
